import { useState, useEffect } from "react";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Link } from "react-router-dom";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMobileOpen(false);
  };

  return (
    <nav
      data-testid="navigation"
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-[#d0dcea] shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => scrollTo("hero")}
          className="flex items-center gap-2.5"
        >
          <img src="/images/logo-circle.webp" alt="Veracity Technologies" className="w-14 h-14 object-contain brightness-200 drop-shadow-[0_0_8px_rgba(0,119,179,0.4)]" />
          <span className={`font-extrabold text-xl tracking-tight transition-colors duration-300 ${scrolled ? "text-[#0a1220]" : "text-white"}`} style={{ fontFamily: "Outfit, sans-serif" }}>
            VERACITY<span className="text-[#0077B3]"> TECHNOLOGIES</span>
          </span>
        </button>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <button data-testid="nav-ai" onClick={() => scrollTo("core-services")} className={`text-sm font-semibold transition-colors ml-4 ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>Services</button>
          <button data-testid="nav-approach" onClick={() => scrollTo("approach")} className={`text-sm font-semibold transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>Approach</button>
          <button data-testid="nav-industries" onClick={() => scrollTo("industries")} className={`text-sm font-semibold transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>Industries</button>
          <button data-testid="nav-compliance" onClick={() => scrollTo("compliance")} className={`text-sm font-semibold transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>Compliance</button>
          <Link to="/business-technology-assessment" data-testid="nav-bta" className={`text-sm font-semibold transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>Assessment</Link>
          <button data-testid="nav-faq" onClick={() => scrollTo("faq")} className={`text-sm font-semibold transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/90 hover:text-white"}`}>FAQ</button>
          <Link to="/cyber-risk-scorecard" data-testid="nav-scorecard" className="text-[#0077B3] hover:text-[#00a0e4] text-sm font-semibold transition-colors">Risk Score</Link>
          <Link to="/human-risk-simulation" data-testid="nav-human-risk-simulation" className="text-[#0077B3] hover:text-[#00a0e4] text-sm font-semibold transition-colors">Human Risk</Link>
          <Link to="/ai-roi-preview" data-testid="nav-roi-calculator" className="text-[#0077B3] hover:text-[#00a0e4] text-sm font-semibold transition-colors">ROI Calculator</Link>
          <a data-testid="nav-phone" href="tel:9529417333" className={`flex items-center gap-2 text-sm transition-colors ${scrolled ? "text-[#0a1220] hover:text-[#0077B3]" : "text-white/80 hover:text-white"}`}>
            <Phone className="w-4 h-4" />
            (952) 941-7333
          </a>
          <Button
            data-testid="nav-cta"
            onClick={() => scrollTo("audit")}
            className="bg-[#00a0e4] text-[#0a1220] hover:bg-[#33b5ea] rounded-sm font-bold text-sm px-5 shadow-[0_0_16px_rgba(0,160,228,0.45)]"
          >
            Get Free Assessment
          </Button>
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className={`md:hidden transition-colors ${scrolled ? "text-[#0a1220]" : "text-white"}`}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#003B71]/98 backdrop-blur-md border-t border-[#003B71] px-6 py-6 space-y-4 animate-fade-in">
          <button data-testid="mobile-nav-why" onClick={() => scrollTo("why-it")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Why Us</button>
          <button data-testid="mobile-nav-approach" onClick={() => scrollTo("approach")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Approach</button>
          <button data-testid="mobile-nav-industries" onClick={() => scrollTo("industries")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Industries</button>
          <button data-testid="mobile-nav-ai" onClick={() => scrollTo("ai-service")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Services</button>
          <button data-testid="mobile-nav-compliance" onClick={() => scrollTo("compliance")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Compliance</button>
          <Link to="/business-technology-assessment" data-testid="mobile-nav-bta" className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Business Technology Assessment</Link>
          <Link to="/human-risk-simulation" data-testid="mobile-nav-human-risk-simulation" className="block text-[#0a1220] hover:text-[#0077B3] text-sm">Human Risk Simulation</Link>
          <Link to="/ai-roi-preview" data-testid="mobile-nav-roi-calculator" className="block text-[#0a1220] hover:text-[#0077B3] text-sm">AI ROI Calculator</Link>
          <button data-testid="mobile-nav-faq" onClick={() => scrollTo("faq")} className="block text-[#0a1220] hover:text-[#0077B3] text-sm">FAQ</button>
          <a href="tel:9529417333" className="flex items-center gap-2 text-[#0a1220] hover:text-[#0077B3] text-sm">
            <Phone className="w-4 h-4" /> (952) 941-7333
          </a>
          <Button
            data-testid="mobile-nav-cta"
            onClick={() => scrollTo("audit")}
            className="w-full bg-[#00a0e4] text-[#0a1220] hover:bg-[#33b5ea] rounded-sm font-bold"
          >
            Get Free Assessment
          </Button>
        </div>
      )}
    </nav>
  );
}
